/**
 * `ApiClient` — único ponto do frontend que fala com a API
 * (`ARCHITECTURE_GUIDE.md`, seção 14: "O frontend faz uma chamada via
 * `core/api-client` para uma rota versionada `/api/v1/...`"). Nenhum
 * `module`/página faz `fetch` diretamente — todos passam por aqui, para
 * que autenticação (cookie de sessão via `credentials: 'include'`),
 * tratamento de erro e formato de resposta fiquem centralizados em um
 * único lugar.
 *
 * Base relativa (`/api/v1`), nunca uma origem absoluta: em
 * desenvolvimento, o proxy do Vite (`vite.config.ts`) encaminha para a
 * API local; em produção, front e API são servidos pela mesma origem
 * (`DEPLOY.md`, seção 15 — local exato do deploy do front ainda em
 * aberto, mas mesma-origem é a suposição de trabalho até uma decisão
 * formal dizer o contrário).
 */

const BASE_URL = '/api/v1';

export type ParametrosConsulta = Record<string, string | number | boolean | undefined>;

/** Formato de erro devolvido por qualquer controller da API (`tratarErro` em cada módulo do backend). */
interface CorpoErroApi {
  erro: string;
  detalhes?: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detalhes?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOpcoes {
  /** Chamado sempre que uma resposta 401 é recebida — quem decide o que fazer (ex: limpar sessão e redirecionar) é quem monta o `ApiClient`, nunca o cliente em si. */
  onNaoAutorizado?: () => void;
}

export class ApiClient {
  constructor(private readonly opcoes: ApiClientOpcoes = {}) {}

  async get<T>(caminho: string, consulta?: ParametrosConsulta): Promise<T> {
    return this.requisitar<T>('GET', caminho, undefined, consulta);
  }

  async post<T>(caminho: string, corpo?: unknown): Promise<T> {
    return this.requisitar<T>('POST', caminho, corpo);
  }

  async patch<T>(caminho: string, corpo?: unknown): Promise<T> {
    return this.requisitar<T>('PATCH', caminho, corpo);
  }

  async put<T>(caminho: string, corpo?: unknown): Promise<T> {
    return this.requisitar<T>('PUT', caminho, corpo);
  }

  async excluir(caminho: string): Promise<void> {
    await this.requisitar<void>('DELETE', caminho);
  }

  /**
   * Upload `multipart/form-data` — único método que não serializa o
   * corpo como JSON (`storage.routes.ts` usa `multer`, que exige o
   * boundary real do `FormData`; o navegador define o header
   * `Content-Type` correto sozinho, nunca escrito à mão aqui). Usado
   * hoje só pelo módulo Galeria (Sprint 12) — nenhum outro fluxo de
   * upload existe ainda no frontend.
   */
  async upload<T>(caminho: string, formData: FormData): Promise<T> {
    const resposta = await fetch(construirUrl(caminho), {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return this.processarResposta<T>(resposta);
  }

  /**
   * GET de um recurso binário (ex: PDF de documento de pedido) — devolve o `Blob` bruto.
   * Mesmo tratamento de 401 e de erro (corpo `{ erro }` → `ApiError`) dos outros métodos;
   * só não tenta parsear a resposta de sucesso como JSON.
   */
  async baixarBlob(caminho: string, consulta?: ParametrosConsulta): Promise<Blob> {
    const resposta = await fetch(construirUrl(caminho, consulta), {
      method: 'GET',
      credentials: 'include',
    });
    if (resposta.status === 401) {
      this.opcoes.onNaoAutorizado?.();
    }
    if (!resposta.ok) {
      throw erroDaResposta(resposta.status, await lerCorpoJson(resposta));
    }
    return resposta.blob();
  }

  private async requisitar<T>(
    metodo: string,
    caminho: string,
    corpo?: unknown,
    consulta?: ParametrosConsulta
  ): Promise<T> {
    const url = construirUrl(caminho, consulta);
    const temCorpo = corpo !== undefined;

    const resposta = await fetch(url, {
      method: metodo,
      credentials: 'include',
      ...(temCorpo ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corpo) } : {}),
    });

    return this.processarResposta<T>(resposta);
  }

  private async processarResposta<T>(resposta: Response): Promise<T> {
    if (resposta.status === 401) {
      this.opcoes.onNaoAutorizado?.();
    }

    if (resposta.status === 204) {
      return undefined as T;
    }

    const corpoResposta = await lerCorpoJson(resposta);

    if (!resposta.ok) {
      throw erroDaResposta(resposta.status, corpoResposta);
    }

    return corpoResposta as T;
  }
}

function construirUrl(caminho: string, consulta?: ParametrosConsulta): string {
  const base = `${BASE_URL}${caminho}`;
  if (!consulta) return base;

  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(consulta)) {
    if (valor !== undefined) params.set(chave, String(valor));
  }
  const querystring = params.toString();
  return querystring ? `${base}?${querystring}` : base;
}

async function lerCorpoJson(resposta: Response): Promise<unknown> {
  const texto = await resposta.text();
  if (!texto) return null;
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

function ehCorpoErroApi(valor: unknown): valor is CorpoErroApi {
  return typeof valor === 'object' && valor !== null && 'erro' in valor;
}

function erroDaResposta(status: number, corpo: unknown): ApiError {
  if (ehCorpoErroApi(corpo)) {
    return new ApiError(status, corpo.erro, corpo.detalhes);
  }
  return new ApiError(status, `erro inesperado (HTTP ${status})`);
}
